// dock.plugin.js
//
// Vue-директива `v-dock` — элемент «висит» плавающей плашкой внизу экрана,
// пока его настоящее место в потоке не доскроллено, и садится в это место
// FLIP-анимацией, когда место входит во вьюпорт. Скролл вверх — возвращается
// на плавающую позицию той же анимацией.
//
//   v-dock                                  — дефолты
//   v-dock="{ offset: 24 }"                 — отступ от нижнего края экрана (px)
//   v-dock="{ disabled: isMobile }"         — реактивно выключить докинг
//
// Опции: offset (px, default 16), zIndex (default 100), duration (ms, default 520),
// threshold / rootMargin — пробрасываются в IntersectionObserver, disabled (bool).
//
// IntersectionObserver берётся из общего registry дизайн-системы
// (utils/intersect.js) — свой observer директива не заводит.
//
// Место в потоке держит плейсхолдер-сиблинг: пока элемент оторван (position:
// fixed), плейсхолдер занимает его высоту, поэтому вёрстка не прыгает и цель
// для observer'а стоит ровно там, где элемент должен приземлиться. Элемент НЕ
// перевешивается в другой DOM-узел — Vue продолжает владеть им как обычно.
//
// Анимация — инлайн-стилями (см. тот же аргумент в reveal.plugin.js: тема
// проекта почти наверняка объявляет свой transition на .card/.button и перебьёт
// класс директивы).

import { observeEl, unobserveEl, prefersReducedMotion } from '../utils/intersect.js';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'; // expo.out — «мягкая посадка»
const state = new WeakMap();

function normalizeOptions(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};

  return {
    offset: o.offset != null ? o.offset : 16,
    zIndex: o.zIndex != null ? o.zIndex : 100,
    duration: o.duration != null ? o.duration : 520,
    threshold: o.threshold != null ? o.threshold : 0,
    rootMargin: o.rootMargin || '0px 0px -12% 0px',
    disabled: !!o.disabled,
  };
}

// Плавающее состояние: fixed по центру снизу. Центрирование — transform,
// поэтому он же становится «базовым» трансформом для FLIP.
const FLOATING_TRANSFORM = 'translateX(-50%)';

function applyFloating(el, opts) {
  el.style.position = 'fixed';
  el.style.left = '50%';
  el.style.right = 'auto';
  el.style.bottom = `calc(${opts.offset}px + env(safe-area-inset-bottom, 0px))`;
  el.style.zIndex = String(opts.zIndex);
  el.style.transform = FLOATING_TRANSFORM;
  el.classList.add('is-floating');
  el.classList.remove('is-docked');
}

function applyDocked(el) {
  el.style.position = '';
  el.style.left = '';
  el.style.right = '';
  el.style.bottom = '';
  el.style.zIndex = '';
  el.style.transform = '';
  el.classList.add('is-docked');
  el.classList.remove('is-floating');
}

// Плейсхолдер держит высоту только пока элемент оторван от потока.
function syncSlot(el, s) {
  s.slot.style.height = s.floating ? `${el.offsetHeight}px` : '0px';
}

// FLIP: меряем «до», меняем состояние, меряем «после», компенсируем разницу
// трансформом и отпускаем его с transition.
function transition(el, s, toFloating, animate) {
  const first = el.getBoundingClientRect();

  el.style.transition = 'none';

  s.floating = toFloating;
  if (toFloating) applyFloating(el, s.opts);
  else applyDocked(el);
  syncSlot(el, s);

  const base = toFloating ? FLOATING_TRANSFORM : '';

  if (!animate || prefersReducedMotion()) {
    el.style.transition = '';
    return;
  }

  const last = el.getBoundingClientRect();
  const dx = first.left - last.left;
  const dy = first.top - last.top;

  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    el.style.transition = '';
    return;
  }

  el.style.transform = `translate(${dx}px, ${dy}px) ${base}`;
  el.getBoundingClientRect(); // форсим reflow, чтобы стартовая позиция закоммитилась

  requestAnimationFrame(() => {
    el.style.transition = `transform ${s.opts.duration}ms ${EASE}`;
    el.style.transform = base;
  });
}

function teardown(el) {
  const s = state.get(el);
  if (!s) return;

  unobserveEl(s.slot);
  window.removeEventListener('resize', s.onResize);
  s.slot.remove();
  applyDocked(el);
  el.style.transition = '';
  state.delete(el);
}

function setup(el, opts) {
  const slot = document.createElement('div');
  slot.className = 'dock-slot';
  slot.setAttribute('aria-hidden', 'true');
  slot.style.pointerEvents = 'none';
  el.parentNode.insertBefore(slot, el);

  console.log('[dock] setup', el.className);
  const s = { slot, opts, floating: true, initialized: false, onResize: null };
  state.set(el, s);

  applyFloating(el, opts);
  syncSlot(el, s);

  s.onResize = () => syncSlot(el, s);
  window.addEventListener('resize', s.onResize, { passive: true });

  observeEl(
    slot,
    {
      once: false,
      // Первый колбэк observer'а — это просто «где мы сейчас», без анимации.
      show: () => {
        console.log('[dock] show', s.floating, s.initialized);
        if (!s.floating) return;
        transition(el, s, false, s.initialized);
        s.initialized = true;
      },
      hide: () => {
        console.log('[dock] hide', s.floating, s.initialized);
        if (s.floating) {
          s.initialized = true;
          return;
        }
        transition(el, s, true, s.initialized);
        s.initialized = true;
      },
    },
    opts
  );
}

const dockDirective = {
  mounted(el, binding) {
    if (typeof window === 'undefined') return; // SSR: директива no-op на сервере

    const opts = normalizeOptions(binding.value);
    if (opts.disabled) return;

    setup(el, opts);
  },

  updated(el, binding) {
    if (typeof window === 'undefined') return;

    const opts = normalizeOptions(binding.value);
    const active = state.has(el);

    if (opts.disabled && active) teardown(el);
    if (!opts.disabled && !active) setup(el, opts);
    if (!opts.disabled && active) state.get(el).opts = opts;
  },

  unmounted(el) {
    if (typeof window === 'undefined') return;
    teardown(el);
  },
};

export default {
  install(app) {
    app.directive('dock', dockDirective);
  },
};

export { dockDirective };
